import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense } from 'react';
import StaffCalculator from './pages/StaffCalculator';
import StaffDashboard from './pages/StaffDashboard';
import LoginPortal from './pages/LoginPortal';
import ProtectedRoute from './components/layout/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import ErrorBoundary from './components/layout/ErrorBoundary';
import { fetchMasterCatalog } from './services/productService';
import { fetchGlobalSettings } from './services/settingsService';
import {
  IS_MOCK_GATEWAY,
  MOCK_CHECKOUT_PATH,
} from './modules/payments/services/mockGatewayService';
import {
  STAFF_ADMIN,
  SALES_MANAGER_FINANCE_PURCHASER,
  ADMIN_FINANCE,
  ADMIN_SALES_MANAGER,
  ADMIN_FINANCE_PURCHASER,
  ADMIN_SALES_PURCHASER,
  ADMIN_PURCHASER,
  MEASURER_ROUTES,
  FABRICATION_MEASUREMENT_ROUTES,
  ALL_ROLES_EXCEPT_STAFF,
  INVOICES_REGISTER_ROLES,
} from './store/roleCohorts';

// Code-split the heavier / less-frequently-loaded pages.
const ClientQuoteView = lazy(() => import('./pages/ClientQuoteView'));
const StaffOnboarding = lazy(() => import('./pages/StaffOnboarding'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const QuotationsDashboard = lazy(() => import('./pages/QuotationsDashboard'));
const PaymentsView = lazy(() => import('./pages/PaymentsView'));
const SalesReportView = lazy(() => import('./pages/SalesReportView'));
const WorkOrderRegisterView = lazy(() => import('./pages/WorkOrderRegisterView'));
// The `lazy(() => import(...))` call itself is NOT tree-shaken away just
// because the binding ends up unused — `React.lazy` isn't annotated
// `/*#__PURE__*/`, so Rollup keeps the call (and the chunk it points at) in a
// prod build even when the <Route> below is gated off. Guarding the
// assignment itself is what lets the dynamic import — and the MockCheckout
// chunk — fold away entirely, PROVIDED the guard is `import.meta.env...`
// checked directly in THIS file. The shared `IS_MOCK_GATEWAY` export
// (imported below, used for the Route guard) does NOT work here: Rollup only
// folds a literal `import.meta.env.X` comparison into a compile-time-provable
// dead branch when it's inline in the same module as the dynamic import — an
// imported cross-module boolean isn't resolved early enough in Rollup's own
// chunk-graph analysis to stop it registering (and emitting) a chunk for the
// dynamic import, even though the LATER per-chunk minify pass can still prove
// the call site itself is unreachable. Confirmed by building with
// VITE_AIRWALLEX_ENV=prod and grepping dist/assets — the chunk (and its page
// content) ships unless this specific check stays inline. See
// CheckoutButton.tsx for the same reasoning applied to `mockCheckoutUrl`.
const MockCheckout =
  import.meta.env.VITE_AIRWALLEX_ENV !== 'prod'
    ? lazy(() => import('./modules/payments/pages/MockCheckout'))
    : null;

// Orders module — the Order entity (list/detail/new/edit) + its OC register.
import {
  OrdersList,
  OrderNew,
  OrderDetail,
  OrderEdit,
  RaiseVariation,
  ApproveVariation,
  OrderConfirmationDocument,
  FinalInvoiceDocument,
  ORDER_ROUTES,
} from './modules/orders/routes';

// Customer module
import {
  CustomersList,
  CustomerDetail,
  CustomerNew,
  CorporateCustomerNew,
  CorporateCustomerDetail,
  CUSTOMER_ROUTES,
} from './modules/customers/routes';

// Vendor module
import { VendorsList, VendorForm, VENDOR_ROUTES } from './modules/vendors/routes';

// Purchase requisitions module
import {
  RequisitionsList,
  RequisitionNew,
  RequisitionDetail,
  REQUISITION_ROUTES,
} from './modules/requisitions/routes';

// Purchase orders module
import {
  PurchaseOrdersList,
  PurchaseOrderNew,
  PurchaseOrderDetail,
  PURCHASE_ORDER_ROUTES,
} from './modules/purchaseOrders/routes';

// Measurement module
import {
  MeasurementsList,
  CustomerScheduling,
  ScheduleView,
  FabricationMeasurement,
  FabricationSheet,
  MEASUREMENT_ROUTES,
} from './modules/measurement/routes';

// Delivery module
import {
  DeliveryDashboard,
  DeliveryNew,
  DeliveryConfirmation,
  RectificationQueue,
  DeliveryNoteDocument,
  DELIVERY_ROUTES,
} from './modules/delivery/routes';

// Claims module
import {
  ClaimsList,
  ClaimDetail,
  ClaimsFinanceQueue,
  ClaimsF5Export,
  PaymentVoucher,
  CLAIM_ROUTES,
} from './modules/claims/routes';

// Cash float module
import {
  CashFloatList,
  CashFloatNew,
  CashFloatLedgerDetail,
  CASH_FLOAT_ROUTES,
} from './modules/cashFloat/routes';

// Work orders module
import { WorkOrdersList, WorkOrderDetail, WORK_ORDER_ROUTES } from './modules/workOrders/routes';

// Receipts module (ADR 0005 Revision 1 — receipt-first inversion).
import {
  ReceiptsList,
  ReceiptNew,
  ReceiptsInbox,
  ReceiptDetail,
  RECEIPT_ROUTES,
} from './modules/receipts/routes';

// App-wide dialog + toast surfaces. Singletons rendered once at the App
// level so both public routes (ClientQuoteView, CustomerScheduling,
// LoginPortal) and authenticated routes can fire dialog.confirm() /
// toast.show() without their own state plumbing.
import { DialogHost, ToastHost } from './components/ui';

/** Redirect legacy /staff/projects* bookmarks to the new /staff/orders* routes,
 *  preserving the rest of the path (id, sub-routes) + query string. */
function LegacyProjectsRedirect() {
  const location = useLocation();
  const to = location.pathname.replace(/^\/staff\/projects/, '/staff/orders') + location.search;
  return <Navigate to={to} replace />;
}

/** 2026-08-27 merge (spec 3.1): the capture page took over the MC detail
 *  route; old /fabrication bookmarks land on the same page. */
const FabricationRedirect = () => {
  const { projectId, mcId } = useParams<{ projectId: string; mcId: string }>();
  return <Navigate to={`/staff/orders/${projectId}/measurements/${mcId}`} replace />;
};

const BootSpinner = () => (
  <div className="min-h-screen bg-background flex justify-center items-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 rounded-full border-4 border-outline-variant border-t-primary animate-spin"></div>
      <div className="text-secondary text-sm font-medium tracking-wide">
        Loading Application Data...
      </div>
    </div>
  </div>
);

export default function App() {
  const [systemBooted, setSystemBooted] = useState(false);

  useEffect(() => {
    // Zero-Latency Bootstrapper: Perform massive DB swap before UI commits.
    const bootSequence = async () => {
      await Promise.all([fetchMasterCatalog(), fetchGlobalSettings()]);
      setSystemBooted(true);
    };
    bootSequence();
  }, []);

  if (!systemBooted) return <BootSpinner />;

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <DialogHost />
        <ToastHost />
        <Suspense fallback={<BootSpinner />}>
          <Routes>
            <Route path="/" element={<Navigate to="/staff/home" replace />} />

            {/* Public Routes */}
            <Route path="/staff/login" element={<LoginPortal />} />
            <Route path="/quote/:quoteId" element={<ClientQuoteView />} />
            <Route path={MEASUREMENT_ROUTES.customerScheduling} element={<CustomerScheduling />} />
            <Route path={DELIVERY_ROUTES.customerConfirmation} element={<DeliveryConfirmation />} />
            <Route path={ORDER_ROUTES.approveVariation} element={<ApproveVariation />} />
            {/* Non-prod mock gateway checkout — dropped entirely from a
                production build. The chunk elimination happens above, at the
                `MockCheckout` assignment (folds to `null` in prod); this
                `IS_MOCK_GATEWAY &&` is a runtime-only belt-and-braces check —
                `MockCheckout` alone would already be `null` in prod — kept so
                this Route stays visibly conditioned on the same flag rather
                than relying solely on a component variable being null. */}
            {IS_MOCK_GATEWAY && MockCheckout && (
              <Route path={MOCK_CHECKOUT_PATH} element={<MockCheckout />} />
            )}
            {/* Legacy redirect: old /staff/projects* bookmarks → /staff/orders* */}
            <Route path="/staff/projects/*" element={<LegacyProjectsRedirect />} />

            {/* Onboarding — any authenticated role */}
            <Route element={<ProtectedRoute />}>
              <Route path="/staff/onboard" element={<StaffOnboarding />} />
            </Route>

            {/* Order Confirmation printable — standalone (no AppLayout chrome)
                so the A4 print is clean, like the quote print. Browseable by
                every internal role that can see a project. */}
            <Route element={<ProtectedRoute allowedRoles={ALL_ROLES_EXCEPT_STAFF} />}>
              <Route path={ORDER_ROUTES.document} element={<OrderConfirmationDocument />} />
              <Route path={ORDER_ROUTES.finalInvoice} element={<FinalInvoiceDocument />} />
              <Route path={DELIVERY_ROUTES.note} element={<DeliveryNoteDocument />} />
            </Route>

            {/* Dashboard — every authoring or back-office role lands here
                or passes through. STAFF (baseline employee) gets only the
                dashboard surface in Phase 0b until /staff/claims ships in
                Phase 1. */}
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'ADMIN',
                    'STAFF',
                    'SALES',
                    'SALES_MANAGER',
                    'FINANCE',
                    'FINANCE_MANAGER',
                    'PURCHASER',
                  ]}
                />
              }
            >
              <Route element={<AppLayout />}>
                <Route path="/staff/home" element={<StaffDashboard />} />
              </Route>
            </Route>

            {/* Sales authorship surfaces — calculator, project create/edit,
                deliveries. ADR 0005 §"Migration path" Phase 0b: STAFF has
                been removed from this cohort and is now claims-only. */}
            <Route element={<ProtectedRoute allowedRoles={STAFF_ADMIN} />}>
              <Route element={<AppLayout />}>
                <Route path="/staff" element={<StaffCalculator />} />
                <Route path={ORDER_ROUTES.new} element={<OrderNew />} />
                <Route path={ORDER_ROUTES.edit} element={<OrderEdit />} />
                <Route path={ORDER_ROUTES.raiseVariation} element={<RaiseVariation />} />
                <Route path={DELIVERY_ROUTES.dashboard} element={<DeliveryDashboard />} />
                <Route path={DELIVERY_ROUTES.new} element={<DeliveryNew />} />
                <Route path={DELIVERY_ROUTES.rectifications} element={<RectificationQueue />} />
              </Route>
            </Route>

            {/* Quotes dashboard — Admin, SALES (sales authors), Finance /
                Finance Manager (reconciliation), and Purchaser (raise-PR
                linkage). STAFF is excluded as of Phase 0b. Quote authorship
                actions (Send, Edit, Measure, Record payment) remain
                role-gated inside the dashboard itself. */}
            <Route element={<ProtectedRoute allowedRoles={SALES_MANAGER_FINANCE_PURCHASER} />}>
              <Route element={<AppLayout />}>
                <Route path="/staff/quotes" element={<QuotationsDashboard />} />
              </Route>
            </Route>

            {/* Read surfaces shared with Finance / Finance Manager AND
                Purchaser — projects list, customers, vendors list,
                requisitions, purchase orders. Page-level controls inside
                each surface gate the write actions. */}
            <Route element={<ProtectedRoute allowedRoles={SALES_MANAGER_FINANCE_PURCHASER} />}>
              <Route element={<AppLayout />}>
                <Route path={ORDER_ROUTES.list} element={<OrdersList />} />
                <Route path={CUSTOMER_ROUTES.list} element={<CustomersList />} />
                {/* Corporate routes must precede the :id detail route so
                    /staff/customers/corporate/* never resolves as :id. */}
                <Route path={CUSTOMER_ROUTES.corporateNew} element={<CorporateCustomerNew />} />
                <Route
                  path={CUSTOMER_ROUTES.corporateDetail}
                  element={<CorporateCustomerDetail />}
                />
                <Route path={CUSTOMER_ROUTES.create} element={<CustomerNew />} />
                <Route path={CUSTOMER_ROUTES.detail} element={<CustomerDetail />} />
                <Route path={VENDOR_ROUTES.list} element={<VendorsList />} />
                <Route path={REQUISITION_ROUTES.list} element={<RequisitionsList />} />
                <Route path={REQUISITION_ROUTES.detail} element={<RequisitionDetail />} />
                <Route path={PURCHASE_ORDER_ROUTES.list} element={<PurchaseOrdersList />} />
                <Route path={PURCHASE_ORDER_ROUTES.detail} element={<PurchaseOrderDetail />} />
              </Route>
            </Route>

            {/* Vendor / manual-PR / PO creation surfaces — SALES + Purchaser.
                Procurement owns vendor records and PR/PO authorship; finance
                reads but doesn't create either. */}
            <Route element={<ProtectedRoute allowedRoles={ADMIN_SALES_PURCHASER} />}>
              <Route element={<AppLayout />}>
                <Route path={VENDOR_ROUTES.new} element={<VendorForm />} />
                <Route path={VENDOR_ROUTES.edit} element={<VendorForm />} />
                <Route path={REQUISITION_ROUTES.new} element={<RequisitionNew />} />
                <Route path={PURCHASE_ORDER_ROUTES.new} element={<PurchaseOrderNew />} />
              </Route>
            </Route>

            {/* Work orders — production authorisations. ADMIN + PURCHASER only
                (production is ops, not sales authorship). */}
            <Route element={<ProtectedRoute allowedRoles={ADMIN_PURCHASER} />}>
              <Route element={<AppLayout />}>
                <Route path={WORK_ORDER_ROUTES.list} element={<WorkOrdersList />} />
                <Route path={WORK_ORDER_ROUTES.detail} element={<WorkOrderDetail />} />
              </Route>
            </Route>

            {/* Payments view — Admin, Finance, Finance Manager. Their
                cross-quote reconciliation surface; no other role needs it.
                FINANCE_MANAGER lands here by default until /staff/claims
                ships in Phase 1 (ADR 0005 §"Migration path"). */}
            <Route element={<ProtectedRoute allowedRoles={ADMIN_FINANCE} />}>
              <Route element={<AppLayout />}>
                <Route path="/staff/payments" element={<PaymentsView />} />
              </Route>
            </Route>

            {/* Invoices register — the SAME PaymentsView, opened on its
                Invoices tab. Wider than /staff/payments by the two sales
                roles: SALES sees only records they authored, SALES_MANAGER
                sees every rep's (row scope lives in paymentsScope.ts, not
                here). SALES_MANAGER, FINANCE and FINANCE_MANAGER are all
                path-fenced, so '/staff/invoices' must stay in each of their
                *_ALLOWED_PREFIXES lists in useAuthStore or the fence bounces
                them off their own register. */}
            <Route element={<ProtectedRoute allowedRoles={INVOICES_REGISTER_ROLES} />}>
              <Route element={<AppLayout />}>
                <Route path="/staff/invoices" element={<PaymentsView />} />
              </Route>
            </Route>

            {/* Sales management report — ADMIN + SALES_MANAGER only. Read-only
                per-rep aggregation; reps do not see each other's numbers.
                SALES_MANAGER is also path-fenced, so '/staff/reports' must stay
                listed in SALES_MANAGER_ALLOWED_PREFIXES (useAuthStore) or the
                fence bounces them despite this gate passing (Lesson #30). */}
            <Route element={<ProtectedRoute allowedRoles={ADMIN_SALES_MANAGER} />}>
              <Route element={<AppLayout />}>
                <Route path="/staff/reports" element={<SalesReportView />} />
              </Route>
            </Route>

            {/* Work Order Register — ADMIN + FINANCE + FINANCE_MANAGER. Read-only
                list of accepted orders with their address-derived legacy WO
                number. FINANCE/FINANCE_MANAGER are path-fenced, so
                '/staff/work-order-register' must stay in their *_ALLOWED_PREFIXES
                (useAuthStore) or the fence bounces them despite this gate
                passing (Lesson #30). */}
            <Route element={<ProtectedRoute allowedRoles={ADMIN_FINANCE} />}>
              <Route element={<AppLayout />}>
                <Route path="/staff/work-order-register" element={<WorkOrderRegisterView />} />
              </Route>
            </Route>

            {/* Measurement surfaces — field crew + sales authors. MEASURER
                lands on /staff/schedule by default. STAFF (baseline) is
                excluded as of Phase 0b. The MC detail route moved out of
                this cohort in the 2026-08-27 merge — see the block below. */}
            <Route element={<ProtectedRoute allowedRoles={MEASURER_ROUTES} />}>
              <Route element={<AppLayout />}>
                <Route path={MEASUREMENT_ROUTES.schedule} element={<ScheduleView />} />
              </Route>
            </Route>

            {/* Fabrication Measurement — the MC detail route IS the merged
                measurement page (2026-08-27 merge, spec
                2026-08-27-measurement-page-merge-design.md): visit header,
                scheduling & admin, per-opening capture, site photos, all in
                one place. Wider than MEASURER_ROUTES on purpose: PURCHASER
                holds the server-side approval cohort
                (approveFabricationMeasurement, ADMIN/PURCHASER) and must be
                able to reach this page to exercise it (Lesson #75 posture);
                as a side effect PURCHASER also sees the scheduling & admin
                section, read-only in practice since its actions keep their
                own per-action gating (spec 3.5 — accepted). See
                FABRICATION_MEASUREMENT_ROUTES' docblock in roleCohorts.ts.
                The old .fabrication path is a deprecated alias that now
                redirects here; .fabricationPrint is unchanged. */}
            <Route element={<ProtectedRoute allowedRoles={FABRICATION_MEASUREMENT_ROUTES} />}>
              <Route element={<AppLayout />}>
                <Route path={MEASUREMENT_ROUTES.detail} element={<FabricationMeasurement />} />
                <Route path={MEASUREMENT_ROUTES.fabrication} element={<FabricationRedirect />} />
                <Route path={MEASUREMENT_ROUTES.fabricationPrint} element={<FabricationSheet />} />
              </Route>
            </Route>

            {/* Project detail is browseable by every signed-in internal role —
                the page itself shows what each can act on. Finance lands here
                when they click into a project from a payment row; Purchaser
                lands here to use the project's "Raise PR" panel. */}
            <Route element={<ProtectedRoute allowedRoles={ALL_ROLES_EXCEPT_STAFF} />}>
              <Route element={<AppLayout />}>
                <Route path={ORDER_ROUTES.detail} element={<OrderDetail />} />
                <Route path={MEASUREMENT_ROUTES.list} element={<MeasurementsList />} />
              </Route>
            </Route>

            {/* Claims — every signed-in role submits + views their own. */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route path={CLAIM_ROUTES.myList} element={<ClaimsList />} />
                <Route path={CLAIM_ROUTES.detail} element={<ClaimDetail />} />
              </Route>
            </Route>

            {/* Receipts (ADR 0005 Revision 1) — every signed-in role submits
                and views their own. Phase A ships staff "My receipts" list +
                multi-upload submit form. Finance inbox + claim assembly land
                in Phase B. STAFF lands here by default after the inversion. */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route path={RECEIPT_ROUTES.myList} element={<ReceiptsList />} />
                <Route path={RECEIPT_ROUTES.new} element={<ReceiptNew />} />
                <Route path={RECEIPT_ROUTES.detail} element={<ReceiptDetail />} />
              </Route>
            </Route>

            {/* Receipt inbox — finance triage + assembly. FINANCE /
                FINANCE_MANAGER / ADMIN only (staff never see other staff's
                receipts). The /staff/receipts prefix is already in the finance
                path-fences, so this is reachable; allowedRoles is the gate. */}
            <Route element={<ProtectedRoute allowedRoles={ADMIN_FINANCE} />}>
              <Route element={<AppLayout />}>
                <Route path={RECEIPT_ROUTES.inbox} element={<ReceiptsInbox />} />
              </Route>
            </Route>

            {/* Claims finance surfaces — FINANCE / FINANCE_MANAGER / ADMIN. */}
            <Route element={<ProtectedRoute allowedRoles={ADMIN_FINANCE} />}>
              <Route element={<AppLayout />}>
                <Route path={CLAIM_ROUTES.financeQueue} element={<ClaimsFinanceQueue />} />
                <Route path={CLAIM_ROUTES.f5Export} element={<ClaimsF5Export />} />
                <Route path={CLAIM_ROUTES.voucher} element={<PaymentVoucher />} />
              </Route>
            </Route>

            {/* Cash float — PURCHASER (their own ledgers via floatHolder flag),
                ADMIN + FINANCE + FINANCE_MANAGER (all ledgers). The list page
                role-gates which ledgers render; provisioning a new ledger is
                admin-only and gated again on the form. */}
            <Route element={<ProtectedRoute allowedRoles={ADMIN_FINANCE_PURCHASER} />}>
              <Route element={<AppLayout />}>
                <Route path={CASH_FLOAT_ROUTES.list} element={<CashFloatList />} />
                <Route path={CASH_FLOAT_ROUTES.ledgerDetail} element={<CashFloatLedgerDetail />} />
              </Route>
            </Route>

            {/* Provisioning a new ledger funds it; admin-only. */}
            <Route element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
              <Route element={<AppLayout />}>
                <Route path={CASH_FLOAT_ROUTES.newLedger} element={<CashFloatNew />} />
              </Route>
            </Route>

            {/* System Administration — admin only */}
            <Route element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
              <Route element={<AppLayout />}>
                <Route path="/staff/admin" element={<AdminDashboard />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
